import { useEffect, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { fmtD } from "./acertoHelpers";

/** Confirmação do acerto: mostra troco que saiu e dinheiro esperado; admin informa o valor conferido e a diferença aparece na hora. */
export function RegistrarAcertoDialog({ open, onClose, entregador, periodo, totais, entregaIds, onDone }) {
  const [conferido, setConferido] = useState("");
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setConferido(totais ? String(totais.a_receber.toFixed(2)) : ""); setObs(""); } }, [open, totais]);
  if (!totais) return null;
  const conf = conferido === "" ? null : Number(conferido);
  const dif = conf == null || Number.isNaN(conf) ? null : Math.round((conf - totais.a_receber) * 100) / 100;
  const confirmar = async () => {
    setBusy(true);
    try {
      const r = await api.post("/acertos", { entregador_id: entregador.id, start: periodo.start, end: periodo.end, entrega_ids: entregaIds,
        valor_conferido: conf != null && !Number.isNaN(conf) ? conf : null, observacao: obs || null });
      toast.success(`Acerto registrado: ${r.data.totais.qtd} entrega(s) · a receber ${fmtBRL(r.data.totais.a_receber)}`);
      onDone?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao registrar acerto"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="acerto-dialog">
        <DialogHeader><DialogTitle>Confirmar acerto — {entregador?.nome}</DialogTitle>
          <DialogDescription>Período {fmtD(periodo.start)} a {fmtD(periodo.end)} · {totais.qtd} entrega(s): {totais.qtd_concluidas} concluída(s), {totais.qtd_canceladas} cancelada(s), {totais.qtd_remarcadas} remarcada(s)</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-orange-300 bg-orange-50 p-3" data-testid="acerto-dialog-troco">
            <div className="text-[11px] font-bold uppercase text-orange-800">Troco que saiu</div>
            <div className="text-2xl font-extrabold mono text-orange-900">{fmtBRL(totais.troco_saiu)}</div>
          </div>
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3" data-testid="acerto-dialog-a-receber">
            <div className="text-[11px] font-bold uppercase text-emerald-800">Dinheiro esperado</div>
            <div className="text-2xl font-extrabold mono text-emerald-900">{fmtBRL(totais.a_receber)}</div>
          </div>
        </div>
        <div className="space-y-1">
          <Label>Dinheiro entregue pelo entregador (conferido)</Label>
          <Input type="number" step="0.01" inputMode="decimal" value={conferido} onChange={(e) => setConferido(e.target.value)} className="mono text-lg h-11" data-testid="acerto-valor-conferido" autoFocus />
          {dif != null && (
            <div className={`text-sm font-bold mono ${dif === 0 ? "text-emerald-700" : dif < 0 ? "text-rose-700" : "text-sky-700"}`} data-testid="acerto-diferenca" data-dif={dif}>
              {dif === 0 ? "✓ Bateu certinho" : dif < 0 ? `Faltam ${fmtBRL(-dif)}` : `Sobra de ${fmtBRL(dif)}`}
            </div>
          )}
        </div>
        <div className="space-y-1">
          <Label>Observação (opcional)</Label>
          <Textarea rows={2} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Ex.: faltou R$ 5 — desconto combinado" data-testid="acerto-observacao" />
        </div>
        <div className="flex gap-2 justify-end pt-1">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={confirmar} disabled={busy || entregaIds.length === 0} className="bg-emerald-700 hover:bg-emerald-800 font-bold" data-testid="acerto-confirmar">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Confirmar acerto
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
